import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {
  __TYPE__,
  COMMUNICATION_WAY,
  CommunicationUnitPackage,
  CommunicationUnitPrice,
  ContactEmail,
  CreditOrder,
  Customer,
  DailyCreditUsageStatistic,
  DATE_FORMAT,
  Invoice,
  InvoiceDetail,
  InvoicePaymentDetails,
  IsoCountryCodeWithPhoneCountryCode,
  LEXOFFICE_BILLING_LANGUAGE,
  LexofficeConnection,
  LexofficeContact,
  MassiveSendingCostCalculation,
  PhoneNumberValidationResponse,
} from '../../models';
import { DateTimeUtils, NumberUtils, SmsUtils, UrlUtils } from '../../utils';
import { ErrorHandlerServiceService } from '../error-handler-service/error-handler-service.service';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';
import { ServerCallerService } from '../server-caller/server-caller.service';

@Injectable({
  providedIn: 'root',
})
export class SmsService {
  private readonly UNSUPPORTED_COUNTRY_CODE = ['AC', 'TA'];

  constructor(
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private serverCaller: ServerCallerService,
    private privateServerCallerService: PrivateServerCallerService
  ) {}

  // region Phone number

  public async validatePhoneNumber(
    phoneNumberToCheck: string,
    isoCode?: string,
    masked?: boolean,
    visibleSuffixLength?: number
  ): Promise<PhoneNumberValidationResponse> {
    const url = UrlUtils.addParameterToUrl('/v2/sms/phone-nr-util', {
      phoneNumberToCheck,
      isoCode,
      masked,
      visibleSuffixLength,
    });

    const response = await this.serverCaller.get(url);
    return response?.body;
  }

  public async getAllCountryCodes(): Promise<
    IsoCountryCodeWithPhoneCountryCode[]
  > {
    const url = `/v1/sms/iso-codes`;

    const response = await this.serverCaller.get(url);

    const data = response?.body || [];
    data.sort((a, b) => {
      try {
        const phoneCodeAsNumberA = Number(a.phoneCountryCode);
        const phoneCodeAsNumberB = Number(b.phoneCountryCode);

        if (phoneCodeAsNumberA < phoneCodeAsNumberB) {
          return -1;
        }

        if (phoneCodeAsNumberA > phoneCodeAsNumberB) {
          return 1;
        }

        return 0;
      } catch (e) {
        return a.phoneCountryCode.localeCompare(b.phoneCountryCode);
      }
    });

    return data?.filter(
      (i) => !this.UNSUPPORTED_COUNTRY_CODE.includes(i.isoCode)
    );
  }

  // endregion

  // region Contact

  public async sendContactEmail(contactEmail: ContactEmail): Promise<boolean> {
    const url = '/v1/contact-emails';

    const response = await this.serverCaller.post(url, contactEmail);
    return response?.status === 201;
  }

  public async sendRwgCancelRequestEmail(
    contactEmail: ContactEmail
  ): Promise<boolean> {
    const url = '/v1/reserve-with-google/opt-out';

    const response = await this.serverCaller.post(url, contactEmail);
    return response?.status === 201;
  }

  // endregion

  // region Statistic

  public async getDailyCreditUsageStatistic(
    businessObjectId: number,
    fromDate: Date,
    toDate: Date
  ): Promise<DailyCreditUsageStatistic[]> {
    const fromDateAsString = DateTimeUtils.formatDateToString(
      fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toDateAsString = DateTimeUtils.formatDateToString(
      toDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${businessObjectId}/sent-sms-statistics/from/${fromDateAsString}/to/${toDateAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body || [];
  }

  // endregion

  // region Cost calculation

  public async calculateNeededCreditForMassiveSending(
    userId: number,
    message: string,
    customers: Customer[],
    preferredCommunicationWay?: COMMUNICATION_WAY
  ): Promise<MassiveSendingCostCalculation> {
    const budget = (await this.getCreditBudget(userId)) || 0;
    const sendingCost =
      (await this.calculateMassiveSendingCost(
        message,
        customers,
        preferredCommunicationWay
      )) || 0;
    const extra = sendingCost - budget || 0;

    return {
      budget: NumberUtils.roundNumber(budget, 2),
      amount: NumberUtils.roundNumber(sendingCost, 2),
      extra: NumberUtils.roundNumber(extra, 2),
    };
  }

  private async calculateMassiveSendingCost(
    message: string,
    customers: Customer[],
    preferredCommunicationWay?: COMMUNICATION_WAY
  ): Promise<number> {
    const url = '/v1/customers/communication-unit-cost';

    try {
      const response = await this.privateServerCallerService.post(url, {
        message,
        recipients: customers?.map((c) => ({
          ...c,
          businessObjectVisitProfiles: undefined, // some fields must be unset. Otherwise error
          appointments: undefined,
          type: undefined,
        })),
        communicationWay: preferredCommunicationWay || undefined,
      });
      return Math.ceil(Number(response?.body));
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
      return 100;
    }
  }

  public async calculateNumberOfRequiredSms(message: string): Promise<number> {
    const url = '/v1/sms/segments';

    const response = await this.privateServerCallerService.post(url, {
      __type__: __TYPE__.BASIC_SMS,
      message,
    });

    return response?.body?.numberOfSms || 0;
  }

  // endregion

  // region Credit

  public async getCreditBudget(ownerId: number): Promise<number> {
    const url = `/v1/business-object-owner/${ownerId}/budget`;

    const response = await this.privateServerCallerService.get(url);

    try {
      return Number(response?.body) || 0;
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
      return 0;
    }
  }

  public async getCreditOrder(creditOrderId: number): Promise<CreditOrder> {
    const url = `/v1/communication-unit-packages/orders/${creditOrderId}`;
    const response = await this.privateServerCallerService.get(url);
    return SmsUtils.transformCreditOrderForFrontend(response?.body);
  }

  public async createCreditOrder(
    creditOrder: CreditOrder,
    returnUrl: string,
    cancelUrl: string
  ): Promise<CreditOrder> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/communication-unit-packages/me/orders`,
      {
        usePaypalPayment: true,
        returnUrl,
        cancelUrl,
      }
    );

    const response = await this.privateServerCallerService.post(
      url,
      creditOrder
    );

    return SmsUtils.transformCreditOrderForFrontend(response?.body);
  }

  public async processOrderWithPaypalPayment(
    creditOrderId: number
  ): Promise<any> {
    const url = `/v1/communication-unit-packages/orders/${creditOrderId}/paypal-payment`;

    const response = await this.privateServerCallerService.patch(url, null);

    return response;
  }

  public async getAllCreditPackages(): Promise<CommunicationUnitPackage[]> {
    const url = `/v1/communication-unit-packages`;
    const response = await this.serverCaller.get(url);

    return SmsUtils.transformCreditPackagesForFrontend(response?.body);
  }

  public async getAllCommunicationUnitPrices(): Promise<
    CommunicationUnitPrice[]
  > {
    const url = `/v1/communication-unit-prices`;

    const response = await this.privateServerCallerService.get(url);

    return SmsUtils.transformCommunicationUnitPricesForFrontend(response?.body);
  }

  // endregion

  // region Invoice

  public async fetchAllLexofficeConnections(): Promise<LexofficeConnection[]> {
    const url = '/v1/user/lexoffice-connections';
    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createLexofficeConnectionAndLexofficeContact(
    lexofficeContact: LexofficeContact,
    lexofficeBillingLanguage: LEXOFFICE_BILLING_LANGUAGE = LEXOFFICE_BILLING_LANGUAGE.EN,
    businessObjectId?: number,
    businessObjectName?: string
  ): Promise<LexofficeConnection> {
    const url = UrlUtils.addParameterToUrl('/v1/user/lexoffice-connection', {
      businessObjectId,
      businessObjectName,
      language: lexofficeBillingLanguage,
    });

    const response = await this.privateServerCallerService.post(
      url,
      lexofficeContact
    );
    return response?.body;
  }

  public async updateLexofficeConnectionAndLexofficeContact(
    lexofficeConnectionId: number,
    lexofficeContact: LexofficeContact,
    lexofficeBillingLanguage: LEXOFFICE_BILLING_LANGUAGE = LEXOFFICE_BILLING_LANGUAGE.EN,
    businessObjectId?: number,
    businessObjectName?: string
  ): Promise<LexofficeConnection> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/user/lexoffice-connection/${lexofficeConnectionId}`,
      {
        businessObjectId,
        businessObjectName,
        language: lexofficeBillingLanguage,
      }
    );

    const response = await this.privateServerCallerService.put(
      url,
      lexofficeContact
    );
    return response?.body;
  }

  public async fetchAllInvoiceOfCurrentLoggedInUser(): Promise<Invoice[]> {
    const url = '/v1/lexoffice-invoice-overview';

    const response = await this.privateServerCallerService.get(url);

    return SmsUtils.transformInvoiceListForFrontend(response?.body);
  }

  public async fetchInvoiceDetails(invoiceId: string): Promise<InvoiceDetail> {
    const url = `/v1/lexoffice-invoice-details?invoiceId=${invoiceId}`;

    const response = await this.privateServerCallerService.get(url);
    return SmsUtils.transformInvoiceDetailForFrontend(response?.body);
  }

  public async fetchAllLexofficeInvoicePayments(): Promise<
    InvoicePaymentDetails[]
  > {
    const url = `/v1/lexoffice-invoice-payments`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async fetchAllInvoiceOfCurrentLoggedInUserWithReconciledStatus(): Promise<
    Invoice[]
  > {
    const [invoices, allInvoicePayments] = await Promise.all([
      this.fetchAllInvoiceOfCurrentLoggedInUser(),
      this.fetchAllLexofficeInvoicePayments(),
    ]);

    const setOfCompletedInvoiceId = new Set<string>(
      allInvoicePayments
        ?.filter((p) => p.paypalOrderStatus === 'COMPLETED')
        ?.map((p) => p.invoiceId)
    );

    invoices?.forEach((i) => {
      if (setOfCompletedInvoiceId.has(i.id)) {
        i.voucherStatus = 'paid';
      }
    });

    return invoices;
  }

  public async downloadInvoicePdf(documentFileId: string): Promise<Blob> {
    const url = `/v1/lexoffice-invoice-pdf?documentFileId=${documentFileId}`;

    const response = await this.privateServerCallerService.get(
      url,
      new HttpHeaders(),
      ServerCallerService.RESPONSE_TYPE.BLOB
    );

    return response?.body;
  }

  public async initiateInvoicePaymentOverPaypal(
    invoiceId: string,
    returnUrl: string,
    cancelUrl: string
  ): Promise<InvoicePaymentDetails> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/lexoffice-invoice-payment-init`,
      {
        invoiceId,
        returnUrl,
        cancelUrl,
      }
    );

    const response = await this.privateServerCallerService.post(url, null);

    return response?.body;
  }

  public async completeInvoicePaymentOverPaypal(
    paymentId: number
  ): Promise<InvoicePaymentDetails> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/lexoffice-invoice-payment-capture`,
      {
        paymentId,
      }
    );

    const response = await this.privateServerCallerService.put(url, null);

    return response?.body;
  }

  // endregion
}
